import { Box, Typography } from "@mui/material";
import theme from "../../theme";

const VARIANTS = {
  dark: {
    background: `${theme.palette.primary.brandSecondary}14`,
    border: `2px solid rgba(255, 255, 255, 0.1)`,
    textColor: "primary.white",
    boxShadow: "4px 4px 12px rgba(255, 255, 255, 0.1)",
    hoverBorder: theme.palette.secondary.tiffanyBlue,
    hoverShadow: `4px 4px 12px ${theme.palette.secondary.tiffanyBlue}70`,
    hoverBackground: undefined, // unchanged on hover in original PFF design
    accentColor: theme.palette.secondary.tiffanyBlue,
    pillBackground: `${theme.palette.secondary.tiffanyBlue}33`,
    pillTextColor: "white",
  },
  light: {
    background: theme.palette.primary.white,
    border: `2px solid ${theme.palette.primary.brandSecondary}4D`, // ~30%
    textColor: "primary.main",
    boxShadow: "none",
    hoverBorder: theme.palette.primary.brandSecondary,
    hoverShadow: `4px 4px 14px ${theme.palette.primary.brandSecondary}59`, // ~35%
    hoverBackground: `${theme.palette.primary.brandSecondary}0D`, // ~5% tint
    accentColor: theme.palette.secondary.tiffanyBlue,
    pillBackground: theme.palette.primary.mistGray,
    pillTextColor: theme.palette.primary.main,
  },
};

/**
 * Shared metric card used across sections (Porter's Five Forces and Spend.
 *
 * @param {string} title - Metric label
 * @param {number} score - Current score value
 * @param {number} [maxScore=5] - Max score, used for the "x/max" label and progress fill
 * @param {string} [label] - Optional text pill (e.g. "High", "Medium"). If omitted, a progress bar renders instead.
 * @param {string|number} [total] - Raw total/KPI value, used instead of score
 * @param {"dark"|"light"} [variant="dark"] - Visual theme; "dark" = PFF-style tinted card, "light" = white-bg card
 */
export const MetricBox = ({
  title,
  score,
  maxScore = 5,
  label,
  total,
  variant = "dark",
}) => {
  const v = VARIANTS[variant];
  const hasScore = score !== undefined && score !== null;
  const progress = hasScore ? Math.min(Math.max(score / maxScore, 0), 1) : 0;

  return (
    <Box
      sx={{
        background: v.background,
        border: v.border,
        color: v.textColor,
        flex: 1,
        display: "flex",
        flexDirection: "column",
        justifyContent: "space-between",
        minHeight: 100,
        height: "100%",
        p: 2.5,
        letterSpacing: 0,
        transition: "all 0.25s ease",
        boxShadow: v.boxShadow,
        borderTopLeftRadius: theme.borderRadius.xxxxl,
        borderBottomRightRadius: theme.borderRadius.xxxxl,
        "&:hover": {
          ...(v.hoverBackground && { backgroundColor: v.hoverBackground }),
          border: `2px solid ${v.hoverBorder}`,
          boxShadow: v.hoverShadow,
          ...(variant === "light" && { transform: "translateY(-2px)" }),
        },
      }}
    >
      <Typography
        variant="body1"
        sx={{ mb: 1, fontWeight: hasScore ? 400 : 500, color: v.textColor }}
      >
        {title}
      </Typography>

      <Box sx={{ display: "flex", gap: 2, mt: 2.5, alignItems: "center" }}>
        {hasScore ? (
          <>
            <Typography
              variant="body2"
              sx={{
                fontWeight: 600,
                color: v.accentColor,
                whiteSpace: "nowrap",
              }}
            >
              {score}/{maxScore}
            </Typography>

            {label ? (
              <Box
                sx={{
                  fontSize: "12px",
                  background: v.pillBackground,
                  color: v.pillTextColor,
                  p: "6px 12px",
                  borderRadius: theme.borderRadius.small,
                  fontWeight: 600,
                  whiteSpace: "nowrap",
                  alignSelf: "flex-start",
                }}
              >
                {label}
              </Box>
            ) : (
              <Box
                sx={{
                  position: "relative",
                  flex: 1,
                  maxWidth: 60,
                  height: 6,
                  borderRadius: theme.borderRadius.full,
                  backgroundColor: v.pillBackground,
                  overflow: "hidden",
                }}
              >
                <Box
                  sx={{
                    position: "absolute",
                    left: 0,
                    top: 0,
                    height: "100%",
                    width: `${progress * 100}%`,
                    borderRadius: theme.borderRadius.full,
                    backgroundColor: v.accentColor,
                    transition: "width 0.3s ease",
                  }}
                />
              </Box>
            )}
          </>
        ) : (
          <Typography
            variant="subtitle2"
            sx={{ fontWeight: 700, color: v.accentColor }}
          >
            {total}
          </Typography>
        )}
      </Box>
    </Box>
  );
};
